import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useCourseTestimonials from '../hooks/useCourseTestimonials';
import styles from './TestimonialsModal.module.css';

const TestimonialsModal = ({ cursoId, onClose }) => {
  const { fetchFull } = useCourseTestimonials(cursoId);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const titleId = `testimonials-title-${cursoId}`;
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const prevFocusedRef = useRef(null);

  useEffect(() => {
    prevFocusedRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      if (closeBtnRef.current) closeBtnRef.current.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    fetchFull(page).then((res) => {
      if (!mounted) return;
      setItems(res.items || []);
      setTotal(res.total || 0);
    }).finally(() => {
      if (mounted) setIsLoading(false);
    });
    return () => { mounted = false; };
  }, [page, fetchFull]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      handleClose(e);
      return;
    }
    if (e.key === 'Tab' && dialogRef.current) {
      const focusable = dialogRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const stopEventPropagation = (e) => {
    e.stopPropagation();
  };

  const handleOverlayClick = (e) => {
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      handleClose(e);
    }
  };

  const handleClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    onClose();
    window.setTimeout(() => {
      if (prevFocusedRef.current && typeof prevFocusedRef.current.focus === 'function') {
        prevFocusedRef.current.focus();
      }
    }, 0);
  };

  const totalPages = Math.ceil((total || 0) / 10);

  const modalContent = (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      onMouseDown={stopEventPropagation}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-labelledby={titleId}
        aria-modal="true"
        ref={dialogRef}
        onKeyDown={handleKeyDown}
        onClick={stopEventPropagation}
        onMouseDown={stopEventPropagation}
      >
        <div className={styles.header}>
          <h3 id={titleId}>Testimonios</h3>
          <button
            type="button"
            className={styles.close}
            onClick={handleClose}
            ref={closeBtnRef}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <div className={styles.content}>
          {isLoading ? (
            <div className={styles.loading}>Cargando...</div>
          ) : items.length === 0 ? (
            <div className={styles.empty}>No hay testimonios</div>
          ) : (
            <ul className={styles.list}>
              {items.map((t, index) => {
                const author = t.author || t.autor || '';
                return (
                  <li key={`testimonial-${t._id || t.id || index}`} className={styles.item}>
                    <blockquote className={styles.quote}>{t.text}</blockquote>
                    <div className={styles.meta}>
                      <span className={styles.author}>{author}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Anterior
            </button>
            <span className={styles.pageInfo}>
              Página {page} de {totalPages}
            </span>
            <button
              type="button"
              className={styles.pageBtn}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default TestimonialsModal;
